import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import {
  ClientRepresentation,
  CredentialRepresentation,
  GroupRepresentation,
  KeycloakAdminApi,
  RoleRepresentation,
  UserRepresentation,
} from '../../services/keycloak-admin-api';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SidebarService } from '../../services/sidebar.service';
import { DatePipe, NgClass } from '@angular/common';
import { formatDistanceToNow } from 'date-fns';

// Helper interface for flattened groups
interface FlattenedGroup extends GroupRepresentation {
  level: number;
  displayName: string;
  isExpanded?: boolean; // Track if this group's children are displayed
  parentId?: string; // Reference to parent group ID for collapsing
}

@Component({
  selector: 'app-user-edit',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, FormsModule, DatePipe, NgClass],
  templateUrl: './user-edit.html',
  styleUrl: './user-edit.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserEdit implements OnInit {
  private keycloakApi = inject(KeycloakAdminApi);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private sidebarService = inject(SidebarService);

  // Input properties for sidebar mode
  @Input() userIdInput?: string;
  @Input() userInput?: UserRepresentation;
  @Input() isSidebarMode = false;

  userId: string | null = null;
  user: UserRepresentation | null = null;
  isLoading = false;
  isSaving = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  activeTab = 'details';
  userForm: FormGroup;
  passwordForm: FormGroup;

  // For role management
  clients: ClientRepresentation[] = [];
  selectedClient: string | null = null;
  availableRoles: RoleRepresentation[] = [];
  assignedRoles: RoleRepresentation[] = [];
  effectiveRoles: RoleRepresentation[] = [];
  isLoadingRoles = false;

  // For group management
  availableGroups: GroupRepresentation[] = [];
  flattenedAvailableGroups: FlattenedGroup[] = [];
  userGroups: GroupRepresentation[] = [];
  isLoadingGroups = false;
  filteredAvailableGroups: FlattenedGroup[] = [];
  groupSearchTerm = '';

  // Track loading states for individual operations
  loadingRoleIds = new Set<string>();
  loadingGroupIds = new Set<string>();

  // For modals
  showDeleteModal = false;
  showPasswordModal = false;

  canEditUser = signal(false);

  constructor() {
    this.userForm = this.fb.group({
      username: ['', Validators.required],
      email: ['', [Validators.email]],
      firstName: [''],
      lastName: [''],
      enabled: [true],
      emailVerified: [false],
    });

    this.passwordForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        passwordConfirm: ['', Validators.required],
        temporary: [true],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    // Determine if we're in sidebar mode or standalone mode
    if (this.isSidebarMode && this.userIdInput) {
      this.userId = this.userIdInput;
      if (this.userInput) {
        // Pre-populate with provided user data
        this.user = this.userInput;
        this.populateForm();
      }
      this.loadUser();
    } else {
      // Standalone mode - get userId from route params
      this.userId = this.route.snapshot.params['id'];
      if (this.userId) {
        this.loadUser();
      }
    }

    // Load clients for role management
    this.loadClients();
  }

  private populateForm(): void {
    if (this.user) {
      this.userForm.patchValue({
        username: this.user.username || '',
        email: this.user.email || '',
        firstName: this.user.firstName || '',
        lastName: this.user.lastName || '',
        enabled: this.user.enabled !== false,
        emailVerified: this.user.emailVerified === true,
      });
    }
  }

  closeSidebar(): void {
    if (this.isSidebarMode) {
      this.sidebarService.closeSidebar();
    }
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const passwordConfirm = form.get('passwordConfirm')?.value;
    return password === passwordConfirm ? null : { mismatch: true };
  }

  setActiveTab(tabEvent: Event): void {
    this.activeTab = (tabEvent as CustomEvent)?.['detail']?.activeTab;

    if (this.activeTab === 'roles' && this.selectedClient) {
      this.loadClientRoles(this.selectedClient);
    } else if (this.activeTab === 'groups') {
      this.loadGroups();
    }
  }

  loadUser(): void {
    if (!this.userId) return;

    this.isLoading = true;
    this.keycloakApi
      .getUserById(this.userId)
      .then((user) => {
        this.user = user;
        this.populateForm();
        this.isLoading = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load user: ${error}`;
        this.isLoading = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  saveUser(): void {
    if (this.userForm.invalid || !this.userId) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const updatedUser: UserRepresentation = {
      username: this.userForm.value.username,
      email: this.userForm.value.email,
      firstName: this.userForm.value.firstName,
      lastName: this.userForm.value.lastName,
      enabled: this.userForm.value.enabled,
      emailVerified: this.userForm.value.emailVerified,
    };

    this.keycloakApi
      .updateUser(this.userId, updatedUser)
      .then(() => {
        this.successMessage = 'User updated successfully';
        this.isSaving = false;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to update user: ${error}`;
        this.isSaving = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Password reset
  openPasswordModal(): void {
    this.passwordForm.reset({ temporary: true });
    this.showPasswordModal = true;
  }

  closePasswordModal(): void {
    this.showPasswordModal = false;
  }

  resetPassword(): void {
    if (this.passwordForm.invalid || !this.userId) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const credential: CredentialRepresentation = {
      type: 'password',
      value: this.passwordForm.value.password,
      temporary: this.passwordForm.value.temporary,
    };

    this.keycloakApi
      .resetPassword(this.userId, credential)
      .then(() => {
        this.successMessage = 'Password reset successfully';
        this.closePasswordModal();
        this.isSaving = false;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to reset password: ${error}`;
        this.isSaving = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Delete user
  openDeleteModal(): void {
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
  }

  deleteUser(): void {
    if (!this.userId) return;

    this.isSaving = true;
    this.keycloakApi
      .deleteUser(this.userId)
      .then(() => {
        this.router.navigate(['/users']);
      })
      .catch((error) => {
        this.errorMessage = `Failed to delete user: ${error}`;
        this.closeDeleteModal();
        this.isSaving = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Client and role management
  loadClients(): void {
    this.keycloakApi
      .getClients()
      .then((clients) => {
        this.clients = clients;
        if (clients.length > 0 && !this.selectedClient) {
          this.selectedClient = clients[0].id || null;
          if (this.activeTab === 'roles' && this.selectedClient) {
            this.loadClientRoles(this.selectedClient);
          }
        }
      })
      .catch((error) => {
        this.errorMessage = `Failed to load clients: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  onClientChange(newValue: string): void {
    this.selectedClient = newValue;
    if (this.selectedClient) {
      this.loadClientRoles(this.selectedClient);
    } else {
      this.availableRoles = [];
      this.assignedRoles = [];
    }
  }

  loadClientRoles(clientId: string): void {
    if (!this.userId) return;

    this.isLoadingRoles = true;

    // Load both available and assigned roles
    Promise.all([
      this.keycloakApi.getAvailableUserClientRoleMappings(this.userId, clientId),
      this.keycloakApi.getUserClientRoleMappings(this.userId, clientId),
      this.keycloakApi.getEffectiveUserClientRoleMappings(this.userId, clientId),
    ])
      .then(([available, assigned, effective]) => {
        this.availableRoles = available;
        this.assignedRoles = assigned;
        this.effectiveRoles = effective;
        this.isLoadingRoles = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load roles: ${error}`;
        this.isLoadingRoles = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  assignRole(role: RoleRepresentation): void {
    if (!this.userId || !this.selectedClient || !role.name || !role.id) return;

    this.loadingRoleIds.add(role.id);
    const roleToAssign: RoleRepresentation = { id: role.id, name: role.name };

    this.keycloakApi
      .addClientRolesToUser(this.userId, this.selectedClient, [roleToAssign])
      .then(() => {
        // Move role from available to assigned
        this.availableRoles = this.availableRoles.filter((r) => r.id !== role.id);
        this.assignedRoles.push(role);
        if (role.id != null) {
          this.loadingRoleIds.delete(role.id);
        }
        this.successMessage = `Role "${role.name}" assigned successfully`;
        if (!this.effectiveRoles.find((r) => r.id === role.id)) {
          this.effectiveRoles.push(role);
        }
      })
      .catch((error) => {
        this.errorMessage = `Failed to assign role: ${error}`;
        if (role.id != null) {
          this.loadingRoleIds.delete(role.id);
        }
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  removeRole(role: RoleRepresentation): void {
    if (!this.userId || !this.selectedClient || !role.name || !role.id) return;

    this.loadingRoleIds.add(role.id);
    const roleToRemove: RoleRepresentation = { id: role.id, name: role.name };

    this.keycloakApi
      .removeClientRolesFromUser(this.userId, this.selectedClient, [roleToRemove])
      .then(() => {
        // Move role from assigned to available
        this.assignedRoles = this.assignedRoles.filter((r) => r.id !== role.id);
        this.availableRoles.push(role);
        if (role.id != null) {
          this.loadingRoleIds.delete(role.id);
        }
        this.successMessage = `Role "${role.name}" removed successfully`;
        if (this.effectiveRoles.find((r) => r.id === role.id) && this.userId) {
          this.keycloakApi
            .getEffectiveUserClientRoleMappings(this.userId, this.selectedClient!)
            .then((effectiveRoles) => {
              this.effectiveRoles = effectiveRoles;
            });
        }
      })
      .catch((error) => {
        this.errorMessage = `Failed to remove role: ${error}`;
        if (role.id != null) {
          this.loadingRoleIds.delete(role.id);
        }
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Group management
  loadGroups(): void {
    if (!this.userId) return;

    this.isLoadingGroups = true;

    // Load both available groups and user groups
    Promise.all([this.keycloakApi.getGroups(), this.keycloakApi.getUserGroups(this.userId)])
      .then(([allGroups, userGroups]) => {
        this.availableGroups = allGroups;
        this.userGroups = userGroups;

        // Flatten the group hierarchy for easier display and filtering
        this.flattenedAvailableGroups = this.flattenGroupHierarchy(allGroups);

        this.filterAvailableGroups();
        this.isLoadingGroups = false;
      })
      .catch((error) => {
        this.errorMessage = `Failed to load groups: ${error}`;
        this.isLoadingGroups = false;
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  // Flatten the group hierarchy into a single-level array with depth information
  flattenGroupHierarchy(
    groups: GroupRepresentation[],
    level = 0,
    parentId?: string
  ): FlattenedGroup[] {
    const result: FlattenedGroup[] = [];

    for (const group of groups) {
      const indentation = '\u00A0\u00A0\u00A0\u00A0'.repeat(level); // Non-breaking spaces for indentation
      const hasChildren = group.subGroups && group.subGroups.length > 0;
      // Add expand/collapse indicator if the group has children
      const displayName = indentation + (hasChildren ? '▶ ' : '\u00A0\u00A0') + (group.name || '');

      // Add the current group with its level
      result.push({
        ...group,
        level: level,
        displayName: displayName,
        isExpanded: false,
        parentId: parentId,
      });

      // Process subgroups if they exist, but don't add them to the initial result
      // They will be added dynamically when expanded
      if (hasChildren) {
        const subgroups = this.flattenGroupHierarchy(group.subGroups ?? [], level + 1, group.id);
        for (const subgroup of subgroups) {
          subgroup.parentId = group.id;
          result.push({ ...subgroup, isExpanded: false });
        }
      }
    }

    return result;
  }

  // Toggle the expanded state of a group and fetch its subgroups if needed
  toggleGroupExpand(group: FlattenedGroup): void {
    // Find the group in our flattened list
    const index = this.flattenedAvailableGroups.findIndex((g) => g.id === group.id);
    console.log(`Toggling group: ${group.name}, index: ${index}`);
    if (index < 0) return;

    const currentGroup = this.flattenedAvailableGroups[index];
    const isCurrentlyExpanded = currentGroup.isExpanded || false;

    // Toggle expanded state
    currentGroup.isExpanded = !isCurrentlyExpanded;

    // Update the display name to show the appropriate indicator
    const hasChildren =
      group.subGroups || (group?.subGroupCount != null && group?.subGroupCount > 0);
    if (hasChildren) {
      const indentation = '\u00A0\u00A0\u00A0\u00A0'.repeat(group.level);
      const indicator = currentGroup.isExpanded ? '▼ ' : '▶ ';
      currentGroup.displayName = indentation + indicator + (group.name || '');
    }

    // If we're expanding and we need to load subgroups
    if (currentGroup.isExpanded && group.id && group.subGroups?.length !== group.subGroupCount) {
      this.isLoadingGroups = true;

      // Fetch the subgroups for this specific group
      this.keycloakApi
        .getGroupChildren(group.id)
        .then((subGroups) => {
          // Process the fetched subgroups
          if (subGroups.length > 0) {
            // Update the original groups data structure
            const originalGroupIndex = this.availableGroups.findIndex((g) => g.id === group.id);
            if (originalGroupIndex >= 0) {
              this.availableGroups[originalGroupIndex].subGroups = subGroups;
            }

            // Create flattened versions of the subgroups
            const flattenedSubgroups = this.flattenGroupHierarchy(
              subGroups,
              group.level + 1,
              group.id
            );

            // Insert the flattened subgroups into our array right after the parent
            this.flattenedAvailableGroups.splice(index + 1, 0, ...flattenedSubgroups);
          }

          this.isLoadingGroups = false;
          this.filterAvailableGroups();
        })
        .catch((error) => {
          this.errorMessage = `Failed to load subgroups: ${error}`;
          this.isLoadingGroups = false;
          setTimeout(() => (this.errorMessage = null), 5000);
        });
    } else {
      // Just re-apply filters to update the display (expanding/collapsing already loaded subgroups)
      this.filterAvailableGroups();
    }
  }

  filterAvailableGroups(): void {
    // Filter available groups to exclude those the user is already in
    const userGroupIds = this.userGroups.map((group) => group.id);

    // Start with all available groups
    const filtered = this.flattenedAvailableGroups.filter(
      (group) => !userGroupIds.includes(group.id)
    );

    // If searching, show all matching groups regardless of hierarchy
    if (this.groupSearchTerm) {
      const term = this.groupSearchTerm.toLowerCase();
      this.filteredAvailableGroups = filtered.filter(
        (group) =>
          group.name?.toLowerCase().includes(term) || group.path?.toLowerCase().includes(term)
      );
      return;
    }

    // Otherwise, only show top level groups and expanded children
    this.filteredAvailableGroups = filtered.filter((group) => {
      // Always show top-level groups (level 0)
      if (group.level === 0) return true;

      // For child groups, only show if their parent is expanded
      if (group.parentId) {
        const parent = this.flattenedAvailableGroups.find((g) => g.id === group.parentId);
        return parent?.isExpanded === true;
      }

      return false;
    });
  }

  onGroupSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.groupSearchTerm = input.value;
    this.filterAvailableGroups();
  }

  addToGroup(group: GroupRepresentation): void {
    if (!this.userId || !group.id) return;

    this.loadingGroupIds.add(group.id);
    this.keycloakApi
      .addUserToGroup(this.userId, group.id)
      .then(() => {
        this.userGroups.push(group);
        this.filterAvailableGroups();
        if (group.id != null) {
          this.loadingGroupIds.delete(group.id);
        }
        this.successMessage = `User added to group "${group.name}" successfully`;
        // setTimeout(() => this.successMessage = null, 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to add user to group: ${error}`;
        if (group.id != null) {
          this.loadingGroupIds.delete(group.id);
        }
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  removeFromGroup(group: GroupRepresentation): void {
    if (!this.userId || !group.id) return;

    this.loadingGroupIds.add(group.id);
    this.keycloakApi
      .removeUserFromGroup(this.userId, group.id)
      .then(() => {
        this.userGroups = this.userGroups.filter((g) => g.id !== group.id);
        this.filterAvailableGroups();
        if (group.id != null) {
          this.loadingGroupIds.delete(group.id);
        }
        this.successMessage = `User removed from group "${group.name}" successfully`;
        setTimeout(() => (this.successMessage = null), 3000);
      })
      .catch((error) => {
        this.errorMessage = `Failed to remove user from group: ${error}`;
        if (group.id != null) {
          this.loadingGroupIds.delete(group.id);
        }
        setTimeout(() => (this.errorMessage = null), 5000);
      });
  }

  invalidateSalesforceCache(): void {
    if (!this.userId) return;

    this.isSaving = true;
    this.keycloakApi
      .invalidateSalesforceCache(this.userId)
      .then(() => {
        this.successMessage = 'Salesforce customer info cache invalidated successfully';
        setTimeout(() => (this.successMessage = null), 3000);
        this.isSaving = false;
        this.loadUser();
      })
      .catch((error) => {
        this.errorMessage = `Failed to invalidate Salesforce cache: ${error}`;
        setTimeout(() => (this.errorMessage = null), 5000);
        this.isSaving = false;
      });
  }

  // Utility methods
  isExternalUser(): boolean {
    return !!this.user?.federationLink;
  }

  getRelativeTime(timestamp: string | number | undefined): string {
    if (!timestamp) return '';

    const date = new Date(typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  }
}
